/** Manifest V2 status labels and transition helpers for service UI. */

export const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "planned", label: "Planned" },
  { value: "dispatched", label: "Dispatched" },
  { value: "in_transit", label: "In transit" },
  { value: "delivered", label: "Delivered" },
  { value: "closed", label: "Closed" },
  { value: "exception", label: "Exception" },
  { value: "cancelled", label: "Cancelled" },
  { value: "reopened", label: "Reopened" },
];

export const COMPLIANCE_PROFILE_OPTIONS = [
  { value: "general_freight", label: "General freight" },
  { value: "household_goods", label: "Household goods" },
  { value: "hazmat", label: "Hazmat" },
  { value: "reefer", label: "Reefer" },
  { value: "dedicated", label: "Dedicated" },
];

export const READINESS_GATES = [
  { value: "build", label: "Build" },
  { value: "dispatch", label: "Dispatch" },
  { value: "depart", label: "Depart" },
  { value: "close", label: "Close" },
  { value: "payout", label: "Payout" },
];

export const EDITABLE_MANIFEST_STATUSES = ["draft", "planned", "reopened"];

/** Next status actions available from each status. */
export const STATUS_ACTIONS = {
  draft: [{ status: "planned", label: "Mark planned" }],
  planned: [{ status: "dispatched", label: "Dispatch", gate: "dispatch" }],
  dispatched: [
    { status: "in_transit", label: "Start transit", gate: "depart" },
    { status: "exception", label: "Mark exception" },
  ],
  in_transit: [
    { status: "delivered", label: "Mark delivered" },
    { status: "exception", label: "Mark exception" },
  ],
  delivered: [
    { status: "closed", label: "Close manifest", gate: "close" },
    { status: "exception", label: "Mark exception" },
  ],
  closed: [{ status: "reopened", label: "Reopen" }],
  exception: [
    { status: "in_transit", label: "Resume transit", gate: "depart" },
    { status: "delivered", label: "Mark delivered" },
    { status: "cancelled", label: "Cancel" },
  ],
  reopened: [
    { status: "planned", label: "Mark planned" },
    { status: "dispatched", label: "Dispatch", gate: "dispatch" },
  ],
  cancelled: [],
};

export function getManifestStatusLabel(status) {
  return STATUS_OPTIONS.find((option) => option.value === status)?.label || status || "Unknown";
}

export function getManifestStatusColor(status) {
  switch (status) {
    case "draft":
      return "gray";
    case "planned":
      return "blue";
    case "dispatched":
      return "indigo";
    case "in_transit":
      return "sky";
    case "delivered":
      return "teal";
    case "closed":
      return "green";
    case "exception":
      return "amber";
    case "cancelled":
      return "red";
    case "reopened":
      return "purple";
    default:
      return "gray";
  }
}

export function getComplianceProfileLabel(profile) {
  return (
    COMPLIANCE_PROFILE_OPTIONS.find((option) => option.value === profile)?.label ||
    profile ||
    "General freight"
  );
}

export function isManifestLegsEditable(status) {
  return EDITABLE_MANIFEST_STATUSES.includes(status);
}

/**
 * Build leg edit path for job show page.
 *
 * @param {object} leg
 * @returns {string|null}
 */
export function getLegJobPath(leg = {}) {
  const jobId = leg.jobId?._id || leg.jobId;
  if (!jobId) return null;
  return `/jobs/${jobId}`;
}

/**
 * Format dispatch snapshot for readable UI (not raw JSON).
 *
 * @param {object} snapshot
 * @returns {object|null}
 */
export function formatDispatchSnapshotSummary(snapshot) {
  if (!snapshot) return null;

  const legs = Array.isArray(snapshot.legs) ? snapshot.legs : [];
  const equipment = Array.isArray(snapshot.equipment) ? snapshot.equipment : [];

  return {
    capturedAt: snapshot.capturedAt,
    manifestNumber: snapshot.manifestNumber,
    complianceProfile: snapshot.complianceProfile,
    legCount: legs.length,
    equipmentCount: equipment.length,
    legs: legs.map((leg, index) => ({
      legId: leg.legId,
      loadNumber: leg.loadNumber || leg.legId,
      jobId: leg.jobId,
      sortOrder: leg.sortOrder ?? index,
      bolNumber: leg.shipping?.bolNumber || "",
      commodity: leg.shipping?.commodity || "",
      isHazmat: Boolean(leg.shipping?.isHazmat),
      mileage: leg.route?.mileage || 0,
    })),
    equipment: equipment.map((item) => ({
      equipmentId: item.equipmentId,
      role: item.role,
      name: item.name,
      vin: item.vin,
      licensePlate: item.licensePlate,
    })),
  };
}

/**
 * Build gate issue display with optional leg link metadata.
 *
 * @param {object} issue
 * @param {object[]} [legs]
 * @returns {object}
 */
export function enrichGateIssue(issue = {}, legs = []) {
  const leg = legs.find((entry) => String(entry._id) === String(issue.legId));
  return {
    ...issue,
    legLabel: leg?.loadNumber || leg?._id || issue.legId,
    jobPath: leg ? getLegJobPath(leg) : null,
  };
}

/**
 * Build create payload for Manifest V2 API.
 *
 * @param {object} form
 * @returns {object}
 */
export function buildManifestV2CreatePayload(form = {}) {
  const payload = {
    driver: form.driver?._id || form.driver,
    status: form.status || "draft",
    note: form.note || "",
  };

  if (form.complianceProfile) {
    payload.complianceProfile = form.complianceProfile;
  }

  if (Array.isArray(form.legIds) && form.legIds.length) {
    payload.legIds = form.legIds;
  } else if (form.startDate && form.endDate) {
    payload.startDate = form.startDate;
    payload.endDate = form.endDate;
  }

  return payload;
}
